import { useEffect, useRef } from 'react';
import { Socket } from 'socket.io-client';

import { ActivationStatus } from '@/entities/vfo/types';
import {
  ApiCaseMemberResponseV2,
  ApiCaseResponseV2,
  ApiDocumentResponseV2,
  ApiTaskResponseV2,
} from '@/shared/api/generated/data-contracts';
import { useCaseSocket } from '@/shared/services/socket/case.service';
import { useAuthStore } from '@/shared/store/auth.store';

import { useCasesStore } from '../stores/cases.store';
import { useDocumentsStore } from '../stores/documents.store';
import { useMembersStore } from '../stores/members.store';
import { useTasksStore } from '../stores/tasks.store';

import { useMembers } from './useMembers';

let isSubscribed = false;

export const useCaseSocketEvents = () => {
  const socket = useCaseSocket();
  const { cases, addCase, updateCase, removeCase } = useCasesStore();
  const { tasks, addTask, updateTask, removeTask } = useTasksStore();
  const { addDocument, updateDocument, removeDocument } = useDocumentsStore();
  const { updateMember, removeMember } = useMembersStore();
  const { account } = useAuthStore();
  const { fetchMembers, getByProfileId, currentUser } = useMembers();

  const casesRef = useRef(cases);

  useEffect(() => {
    casesRef.current = cases;
  }, [cases]);

  const checkMember = (profileId: number) => {
    if (!getByProfileId(profileId)) {
      fetchMembers();
      return true;
    }
    return false;
  };

  const caseCreated = (data: ApiCaseResponseV2) => {
    if (data.profileId === account?.id) return;
    addCase(data);
  };

  const caseUpdated = (data: ApiCaseResponseV2) => {
    if (data.profileId === account?.id) return;
    updateCase(data.id, data);
  };
  const caseDeleted = (data: { id: string }) => {
    removeCase(data.id);
  };

  const membersStatusChanged = (data: {
    tenantProfileId: string;
    profileId: number;
    status: ApiCaseMemberResponseV2['status'];
  }) => {
    if (checkMember(data.profileId)) return;
    updateMember(data.profileId, {
      activationStatus:
        data.status === 'REGISTERED' ? ActivationStatus.ACTIVE : ActivationStatus.PENDING,
    });
  };

  const membersAccessLevelChanged = (data: {
    profileId: number;
    roleCode: ApiCaseMemberResponseV2['accessLevel'];
  }) => {
    if (checkMember(data.profileId)) return;
    updateMember(data.profileId, {
      accessLevel: data.roleCode,
    });
  };

  const caseMemberCreated = (data: ApiCaseMemberResponseV2) => {
    checkMember(data.profileId);
  };

  const caseMemberUpdated = (data: ApiCaseMemberResponseV2) => {
    if (checkMember(data.profileId)) return;
    updateMember(data.profileId, {
      activationStatus:
        data.status === 'REGISTERED' ? ActivationStatus.ACTIVE : ActivationStatus.PENDING,
      country: data.countryCode ?? undefined,
      profileId: data.profileId,
    });
  };
  const caseMemberDeleted = (data: { caseId: string; profileId: number }) => {
    const isStillInOtherCases = cases.some(
      (c) => c.id !== data.caseId && c.members.some((m) => m.profileId === data.profileId),
    );

    if (!isStillInOtherCases) {
      removeMember(data.profileId);
    }
  };

  const documentCreated = (data: ApiDocumentResponseV2) => {
    if (data.caseMember.profileId === account?.id) return;
    addDocument(data);
    data.caseDocuments.forEach((item) => {
      const caseData = casesRef.current.find((c) => c.id === item.caseId);
      if (!caseData) return;
      updateCase(item.caseId, {
        caseDocuments: [...caseData.caseDocuments.filter((d) => d.id !== item.id), item],
      });
    });
  };

  const documentUpdated = (data: ApiDocumentResponseV2) => {
    if (data.caseMember.profileId === account?.id) return;
    updateDocument(data.id, data);
  };

  const documentRemovedFromCase = (data: ApiDocumentResponseV2) => {
    if (data.caseMember.profileId === account?.id) return;
    updateDocument(data.id, data);
    casesRef.current.forEach((caseData) => {
      if (
        caseData.caseDocuments.some((doc) => doc.documentId === data.id) &&
        data.caseDocuments.every((doc) => doc.caseId !== caseData.id)
      ) {
        updateCase(caseData.id, {
          caseDocuments: caseData.caseDocuments.filter((doc) => doc.documentId !== data.id),
        });
      }
    });
  };

  const documentDeleted = (data: { id: string; caseIds: string[] }) => {
    removeDocument(data.id);
    data.caseIds.forEach((item) => {
      const caseData = casesRef.current.find((c) => c.id === item);
      if (!caseData) return;
      updateCase(item, {
        caseDocuments: [...caseData.caseDocuments.filter((d) => d.id !== data.id)],
      });
    });
  };

  const taskCreated = (data: ApiTaskResponseV2) => {
    if (data.creator.profileId === account?.id || !currentUser?.profileId) return;
    const task = tasks.find((existingTask) => existingTask.id === data.id);
    if (task) return;

    addTask(data, currentUser.profileId);
    const caseData = casesRef.current.find((c) => c.id === data.caseId);
    if (!caseData) return;

    updateCase(data.caseId, {
      tasks: [...caseData.tasks.filter((t) => t.id !== data.id), data],
    });
  };

  const taskUpdated = (data: ApiTaskResponseV2) => {
    if (data.creator.profileId === account?.id) return;
    updateTask(data.id, data);
  };

  const taskDeleted = (data: { id: string; caseId: string }) => {
    removeTask(data.id);
    const caseData = casesRef.current.find((c) => c.id === data.caseId);
    if (!caseData) return;
    updateCase(data.caseId, {
      tasks: [...caseData.tasks.filter((t) => t.id !== data.id)],
    });
  };

  const subscribeToWebSocket = (subSocket: Socket) => {
    subSocket.on('case:created', caseCreated);
    subSocket.on('case:updated', caseUpdated);
    subSocket.on('case:deleted', caseDeleted);
    subSocket.on('members:status_changed', membersStatusChanged);
    subSocket.on('members:access_level_changed', membersAccessLevelChanged);
    subSocket.on('case-member:created', caseMemberCreated);
    subSocket.on('case-member:updated', caseMemberUpdated);
    subSocket.on('case-member:deleted', caseMemberDeleted);
    subSocket.on('document:created', documentCreated);
    subSocket.on('document:updated', documentUpdated);
    subSocket.on('document:deleted', documentDeleted);
    subSocket.on('document:removed-from-case', documentRemovedFromCase);
    subSocket.on('task:created', taskCreated);
    subSocket.on('task:updated', taskUpdated);
    subSocket.on('task:deleted', taskDeleted);
  };

  const unSubscribeFromWebSocket = (subSocket: Socket) => {
    subSocket.off('case:created', caseCreated);
    subSocket.off('case:updated', caseUpdated);
    subSocket.off('case:deleted', caseDeleted);
    subSocket.off('members:status_changed', membersStatusChanged);
    subSocket.off('members:access_level_changed', membersAccessLevelChanged);
    subSocket.off('case-member:created', caseMemberCreated);
    subSocket.off('case-member:updated', caseMemberUpdated);
    subSocket.off('case-member:deleted', caseMemberDeleted);
    subSocket.off('document:created', documentCreated);
    subSocket.off('document:updated', documentUpdated);
    subSocket.off('document:deleted', documentDeleted);
    subSocket.off('document:removed-from-case', documentRemovedFromCase);
    subSocket.off('task:created', taskCreated);
    subSocket.off('task:updated', taskUpdated);
    subSocket.off('task:deleted', taskDeleted);
  };

  useEffect(() => {
    if (socket && !isSubscribed) {
      isSubscribed = true;
      subscribeToWebSocket(socket);
    }

    return () => {
      if (socket && isSubscribed) {
        isSubscribed = false;
        unSubscribeFromWebSocket(socket);
      }
    };
  }, [socket]);
};
